import { FC, Fragment, ReactElement } from 'react';

import { get, useFormContext } from 'react-hook-form';
import { FormFeedback } from 'reactstrap';

import classnames from 'classnames';

import { isDefined, isEmpty } from '@hsp/utilities';

import { DonationHigh, DonationLow } from 'pages/audience/shared';
import { IFormDataPoint } from 'types/api/audience';
import { SearchType } from 'types/api/audience/enums';
import { ITagSelectionDataPointProps } from 'types/pages/audience-builder/components';
import { getFormattedString, s } from 'utilities/strings';

const lowValidationMessage = getFormattedString('Site.Validation.LessThan', 'Site.Low', 'Site.Cap');
const highValidationMessage = getFormattedString('Site.Validation.MoreThan', 'Site.Cap', 'Site.Low');
const numberMessage = s('Site.Validation.InvalidNumber');

const Donation: FC<ITagSelectionDataPointProps> = ({ data }) => {
  const {
    formState: { errors },
    register,
    getValues,
    setValue,
    trigger,
  } = useFormContext();
  const { children } = data;
  let Component: ReactElement;

  if (!isDefined(children) || isEmpty(children) || children.some((child) => typeof child === 'string')) {
    Component = <Fragment>{null}</Fragment>;
  } else {
    const high = children[1] as IFormDataPoint;
    const low = children[0] as IFormDataPoint;
    const { formName: highFormName, type: highDataPointType } = high;
    const { formName: lowFormName, type: lowDataPointType } = low;
    const modifiedHighFormName = `${SearchType.Basic}.${highFormName}`;
    const modifiedLowFormName = `${SearchType.Basic}.${lowFormName}`;
    const highType = `${modifiedHighFormName}.type`;
    const lowType = `${modifiedLowFormName}.type`;

    Component = (
      <div className="rfm-donation-wrapper">
        <div>
          <DonationLow
            className={classnames({
              'is-invalid': get(errors, lowType) === 'lessThanHigh' || get(errors, lowType) === 'isNumber',
            })}
            data={low}
            isInvalid={get(errors, lowType) === 'lessThanHigh' || get(errors, lowType) === 'isNumber'}
            onValueChanged={(controlValue) => {
              if (isDefined(low)) {
                register(modifiedLowFormName, {
                  validate: {
                    isNumber: ({ value }) => {
                      if (!isDefined(value) || isEmpty(value)) {
                        return true;
                      }

                      const valid = !isNaN(Number(value));

                      return valid || numberMessage;
                    },
                    lessThanHigh: ({ value }) => {
                      if (!isDefined(value) || isEmpty(value)) {
                        return true;
                      }

                      const compare = getValues(modifiedHighFormName);

                      if (!isDefined(compare) || isEmpty(compare.value)) {
                        return true;
                      }

                      const valid = Number(value) < Number(compare.value);

                      return valid || lowValidationMessage;
                    },
                  },
                });
                setValue(
                  modifiedLowFormName,
                  {
                    operator: '>=',
                    type: lowDataPointType,
                    value: controlValue,
                  },
                  {
                    shouldDirty: true,
                    shouldTouch: true,
                    shouldValidate: true,
                  }
                );
                trigger(modifiedLowFormName);
              }
            }}
            value={getValues(`${modifiedLowFormName}.value`)}
          />
          <FormFeedback type="invalid">{get(errors, `${modifiedLowFormName}.message`)}</FormFeedback>
        </div>
        <div>
          <DonationHigh
            className={classnames({
              'is-invalid': get(errors, highType) === 'moreThanLow' || get(errors, highType) === 'isNumber',
            })}
            data={high}
            isInvalid={get(errors, highType) === 'moreThanLow' || get(errors, highType) === 'isNumber'}
            onValueChanged={(controlValue) => {
              if (isDefined(high)) {
                register(modifiedHighFormName, {
                  validate: {
                    isNumber: ({ value }) => {
                      if (!isDefined(value) || isEmpty(value)) {
                        return true;
                      }

                      const valid = !isNaN(Number(value));

                      return valid || numberMessage;
                    },
                    moreThanLow: ({ value }) => {
                      if (!isDefined(value) || isEmpty(value)) {
                        return true;
                      }

                      const compare = getValues(modifiedLowFormName);

                      if (!isDefined(compare) || isEmpty(compare.value)) {
                        return true;
                      }

                      const valid = Number(value) > Number(compare.value);

                      return valid || highValidationMessage;
                    },
                  },
                });
                setValue(
                  modifiedHighFormName,
                  {
                    operator: '<=',
                    type: highDataPointType,
                    value: controlValue,
                  },
                  {
                    shouldDirty: true,
                    shouldTouch: true,
                    shouldValidate: true,
                  }
                );
                trigger(modifiedHighFormName);
              }
            }}
            value={getValues(`${modifiedHighFormName}.value`) ?? ''}
          />
          <FormFeedback type="invalid">{get(errors, `${modifiedHighFormName}.message`)}</FormFeedback>
        </div>
      </div>
    );
  }

  return Component;
};

export default Donation;
